
import React from 'react';
import { VideoEvent } from '@/hooks/useVideoEvents';
import { Button } from '@/components/ui/button';
import { Info } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface VideoPreviewProps {
  video: VideoEvent;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({ video }) => {
  const formattedDate = video.date ? 
    format(video.date, 'd MMMM yyyy à HH:mm', { locale: fr }) : 
    'Date inconnue';
  
  const handleExternalButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open(`https://www.youtube.com/watch?v=${video.videoId}`, '_blank');
  };
    
  return (
    <div className="w-full p-4 overflow-y-auto">
      <div className="mb-4">
        <h3 className="text-lg font-medium mb-2">Aperçu de la publication</h3>
        <div className="flex justify-between items-center mb-4">
          <Button 
            variant="link" 
            className="text-blue-600 p-0 h-auto"
            onClick={handleExternalButtonClick}
          >
            Consulter sur YouTube <span className="ml-1">↗</span>
          </Button>
        </div>
        
        <div className="border rounded-md overflow-hidden">
          {video.videoId ? (
            <div className="aspect-video bg-black relative">
              <img 
                src={`https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`} 
                alt={video.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`;
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div 
                  className="w-14 h-14 bg-black bg-opacity-70 rounded-full flex items-center justify-center cursor-pointer"
                >
                  <div className="ml-1 w-0 h-0 border-y-[10px] border-l-[16px] border-y-transparent border-l-white"></div>
                </div>
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-2 flex justify-between items-center">
                <div className="flex items-center">
                  <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse mr-2"></div>
                  <span className="text-xs">0:00</span>
                </div>
                <div className="flex items-center gap-2">
                  <button className="w-8 h-8 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded-full">
                    <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="currentColor" d="M5,3H19A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3M13,13V7H11V13H7V15H11V19H13V15H17V13H13Z"></path></svg>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded-full">
                    <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="currentColor" d="M12,3V5H8V9h2v7a3,3 0 0,0 3,3a3,3 0 0,0 3,-3V9h2V5h-4V3H12z"></path></svg>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded-full">
                    <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="currentColor" d="M2 4h20v16H2z"></path></svg>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="aspect-video bg-gray-200 flex items-center justify-center">
              <p className="text-gray-500">Aucun aperçu disponible</p>
            </div>
          )}
        </div>
      </div>
      
      <div className="border rounded-md p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-red-600 w-8 h-8 rounded-md flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-white"><path fill="currentColor" d="M10,15L16,12L10,9V15M21.56,7.17C21.69,7.64 21.78,8.27 21.84,9.07C21.91,9.87 21.94,10.56 21.94,11.16L22,12C22,14.19 21.84,15.8 21.56,16.83C21.31,17.73 20.73,18.31 19.83,18.56C19.36,18.69 18.5,18.78 17.18,18.84C15.88,18.91 14.69,18.94 13.59,18.94L12,19C7.81,19 5.2,18.84 4.17,18.56C3.27,18.31 2.69,17.73 2.44,16.83C2.31,16.36 2.22,15.73 2.16,14.93C2.09,14.13 2.06,13.44 2.06,12.84L2,12C2,9.81 2.16,8.2 2.44,7.17C2.69,6.27 3.27,5.69 4.17,5.44C4.64,5.31 5.5,5.22 6.82,5.16C8.12,5.09 9.31,5.06 10.41,5.06L12,5C16.19,5 18.8,5.16 19.83,5.44C20.73,5.69 21.31,6.27 21.56,7.17Z"></path></svg>
          </div>
          <div>
            <h4 className="text-base font-semibold truncate">{video.title}</h4>
            <p className="text-xs text-gray-500">{formattedDate}</p>
          </div>
        </div>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-md border-l-4 border-blue-400">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-blue-500 mt-0.5" />
          <p className="text-sm text-blue-800">
            Les aperçus de publication donnent une idée de la manière dont votre contenu s'affiche quand il est publié. Les tests et mises à jour des réseaux sociaux peuvent affecter le rendu réel.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VideoPreview;
